import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/common";
import { PilotageBoard } from "@/components/PilotageBoard";
import { CoverThumb } from "@/components/CoverThumb";
import { STATUS_META, PRESTATIONS, fmtDate } from "@/lib/helpers";
import * as Icons from "lucide-react";

const BANNER = "/signa-dash-banner.webp?v=3";

const CARDS = [
  { key: "brouillon", icon: "FilePen", color: "text-amber-600 bg-amber-50" },
  { key: "a_verifier", icon: "SearchCheck", color: "text-blue-600 bg-blue-50" },
  { key: "a_completer", icon: "ClipboardList", color: "text-orange-600 bg-orange-50" },
  { key: "validee", icon: "CircleCheck", color: "text-emerald-600 bg-emerald-50" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [signedDevis, setSignedDevis] = useState([]);
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const canCreate = hasPerm("visits.create");
  const canTrack = hasPerm("track.manage");
  const firstName = (user?.name || "").trim().split(" ")[0] || "";

  const loadStats = () => { setError(false); setData(null); api.get("/dashboard/stats").then((r) => setData(r.data)).catch(() => setError(true)); };
  useEffect(() => {
    loadStats();
    api.get("/devis/signed-pending").then((r) => setSignedDevis(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Bandeau d'accueil */}
      <div className="relative overflow-hidden rounded-2xl bg-[#0D2E24] shadow-sm" data-testid="welcome-banner">
        <img src={BANNER} alt="" aria-hidden="true" style={{ objectPosition: "58% 22%" }} className="absolute inset-y-0 right-0 h-full w-3/5 object-cover sm:w-2/3" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D2E24] from-30% via-[#0D2E24]/85 via-60% to-transparent" aria-hidden="true" />
        <div className="relative z-10 flex min-h-[92px] flex-col justify-center px-5 py-4 sm:min-h-[110px] sm:px-7">
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl" data-testid="welcome-greeting">
            Bonjour{firstName ? ` ${firstName}` : ""}
          </h1>
          <p className="mt-0.5 text-sm text-white/85 sm:text-base">Vos chantiers et vos visites, en un coup d'œil.</p>
        </div>
      </div>

      {/* Pilotage des chantiers */}
      <section className="space-y-4" data-testid="pilotage-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Pilotage des chantiers</h2>
          <div className="flex w-full gap-2 sm:w-auto">
            {canCreate && <Button variant="outline" onClick={() => navigate("/check?new=1")} data-testid="dashboard-new-visit"
              className="h-10 flex-1 border-emerald-500 text-emerald-700 hover:bg-emerald-50 sm:flex-none"><Icons.Plus className="mr-1.5 h-4 w-4" />Nouvelle visite</Button>}
            {canTrack && <Button onClick={() => navigate("/track?new=1")} data-testid="dashboard-new-worksite"
              className="h-10 flex-1 bg-emerald-600 hover:bg-emerald-700 sm:flex-none"><Icons.Plus className="mr-1.5 h-4 w-4" />Nouveau chantier</Button>}
          </div>
        </div>
        <PilotageBoard />
      </section>

      {/* Visites : prochaines + suivi */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Card className="overflow-hidden" data-testid="upcoming-card">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3.5 sm:px-5">
            <h2 className="flex items-center gap-2 font-display text-base font-semibold text-[#0D2E24] sm:text-lg">
              <Icons.CalendarClock className="h-5 w-5 text-emerald-600" /> Prochaines visites
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/check")} data-testid="upcoming-see-all" className="text-emerald-700">
              Voir les visites <Icons.ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          {!data && !error ? (
            <div className="p-6 text-center text-sm text-slate-400">Chargement…</div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 p-6 text-center"><p className="text-sm text-rose-600">Impossible de charger les visites.</p>
              <Button variant="outline" size="sm" onClick={loadStats} data-testid="visits-retry">Réessayer</Button></div>
          ) : !data.upcoming?.length ? (
            <div className="flex flex-col items-center px-4 py-7 text-center" data-testid="upcoming-empty">
              <Icons.CalendarDays className="h-9 w-9 text-emerald-500/70" />
              <p className="mt-2 font-semibold text-slate-800">Aucune visite programmée</p>
              {canCreate && <Button variant="outline" onClick={() => navigate("/check?new=1")} data-testid="upcoming-create"
                className="mt-4 border-emerald-300 text-emerald-700 hover:bg-emerald-50">Créer une visite</Button>}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.upcoming.slice(0, 4).map((v) => (
                <li key={v.id}>
                  <button onClick={() => navigate(`/check/${v.id}`)} data-testid={`upcoming-visit-${v.reference}`}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 sm:px-5">
                    <CoverThumb path={v.site_cover_file_id ? `/files/${v.site_cover_file_id}/download` : null} alt={v.site_name} icon="ClipboardCheck" tile="bg-violet-50 text-violet-600" testId={`upcoming-${v.reference}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-800">Visite · {v.client_name || "—"}</p>
                      <p className="truncate text-sm text-slate-500">{v.site_name} · {fmtDate(v.date)}{v.scheduled_time ? ` à ${v.scheduled_time}` : ""}</p>
                    </div>
                    <Icons.ChevronRight className="h-4 w-4 text-slate-300" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4 sm:p-5" data-testid="visit-stats-card">
          <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-[#0D2E24] sm:text-lg">
            <Icons.ClipboardList className="h-5 w-5 text-emerald-600" /> Suivi des visites
          </h2>
          <div className="grid grid-cols-2 gap-2.5">
            {CARDS.map((c) => {
              const I = Icons[c.icon];
              const val = error ? "—" : data ? (data.counts?.[c.key] ?? 0) : null;
              return (
                <button key={c.key} onClick={() => navigate(`/check?status=${c.key}`)}
                  className="flex items-center gap-2.5 rounded-xl border border-slate-200 p-2.5 text-left transition-shadow hover:shadow-md" data-testid={`stat-${c.key}`}>
                  <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${c.color}`}><I className="h-4 w-4" /></span>
                  <span className="min-w-0">
                    <span className="block font-mono-data text-xl font-bold leading-tight text-slate-900" data-testid={`stat-count-${c.key}`}>
                      {val === null ? <span className="inline-block h-5 w-5 animate-pulse rounded bg-slate-200 align-middle" /> : val}</span>
                    <span className="block truncate text-xs text-slate-500">{STATUS_META[c.key].label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Devis signés — à traiter */}
      {signedDevis.length > 0 && (
        <Card className="overflow-hidden" data-testid="signed-devis-card">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3.5 sm:px-5">
            <h2 className="flex items-center gap-2 font-display text-base font-semibold text-[#0D2E24] sm:text-lg">
              <Icons.PenLine className="h-5 w-5 text-emerald-600" /> Devis signés — à traiter
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white" data-testid="signed-devis-count">{signedDevis.length}</span>
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/devis")} className="text-emerald-700" data-testid="signed-devis-all">
              Voir tout <Icons.ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {signedDevis.map((d) => (
              <button key={d.id} onClick={() => navigate("/devis")} data-testid={`signed-devis-${d.id}`}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 sm:px-5">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                  <Icons.FileSignature className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-800">{d.title} · <span className="font-mono-data text-xs text-slate-400">{d.reference}</span></p>
                  <p className="truncate text-sm text-slate-500">{d.client_name} · {d.site_name} — signé par {d.accepted_by_name || "le client"}</p>
                </div>
                <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Signé</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* SIGNA TRACK — Rappels internes */}
      {data && ((data.track_requests?.length || 0) > 0 || (data.track_deadlines?.length || 0) > 0) && (
        <Card className="overflow-hidden" data-testid="track-reminders-card">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3.5 sm:px-5">
            <h2 className="flex items-center gap-2 font-display text-base font-semibold text-[#0D2E24] sm:text-lg">
              <Icons.HardHat className="h-5 w-5 text-emerald-600" /> SIGNA TRACK — à traiter
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/track")} className="text-emerald-700" data-testid="track-reminders-all">
              Voir tout <Icons.ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {data.track_requests?.map((r) => (
              <button key={r.id} onClick={() => navigate(`/track/${r.worksite_id}`)} data-testid={`track-req-${r.id}`}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 sm:px-5">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
                  <Icons.MessageSquareDot className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-800">{r.kind === "probleme" ? "Signalement" : "Prestation complémentaire"} · <span className="font-mono-data text-xs text-slate-400">{r.worksite_ref}</span></p>
                  <p className="truncate text-sm text-slate-500">{r.description}</p>
                </div>
                <span className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-700">Demande</span>
              </button>
            ))}
            {data.track_deadlines?.map((w) => (
              <button key={w.id} onClick={() => navigate(`/track/${w.id}`)} data-testid={`track-deadline-${w.id}`}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 sm:px-5">
                <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${w.overdue ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-600"}`}>
                  <Icons.CalendarClock className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-800"><span className="font-mono-data text-xs text-slate-400">{w.reference}</span> · {w.title}</p>
                  <p className="truncate text-sm text-slate-500">Fin prévue : {fmtDate(w.planned_end)}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${w.overdue ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{w.overdue ? "En retard" : "Échéance"}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* SIGNA CARE — Échéances */}
      {data && (data.care_deadlines?.length || 0) > 0 && (
        <Card className="overflow-hidden" data-testid="care-deadlines-card">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3.5 sm:px-5">
            <h2 className="flex items-center gap-2 font-display text-base font-semibold text-[#0D2E24] sm:text-lg">
              <Icons.ShieldCheck className="h-5 w-5 text-emerald-600" /> SIGNA CARE — échéances
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/care?echeance=due")} className="text-emerald-700" data-testid="care-deadlines-all">
              Voir tout <Icons.ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {data.care_deadlines.map((e) => (
              <button key={e.id} onClick={() => navigate(`/care/${e.id}`)} data-testid={`care-deadline-${e.id}`}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 sm:px-5">
                <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${e.overdue ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-600"}`}>
                  <Icons.CalendarClock className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-800"><span className="font-mono-data text-xs text-slate-400">{e.reference}</span> · {e.name}</p>
                  <p className="truncate text-sm text-slate-500">{e.site_name ? `${e.site_name} · ` : ""}Contrôle : {fmtDate(e.next_check_date)}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${e.overdue ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{e.overdue ? "En retard" : "Échéance"}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Dernières visites */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3.5 sm:px-5">
          <h2 className="flex items-center gap-2 font-display text-base font-semibold text-[#0D2E24] sm:text-lg">
            <Icons.FileText className="h-5 w-5 text-emerald-600" /> Dernières visites
          </h2>
          <Button variant="ghost" size="sm" onClick={() => navigate("/check")} data-testid="dashboard-see-all" className="text-emerald-700">
            Voir tout <Icons.ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        {!data && !error ? (
          <div className="p-6 text-center text-sm text-slate-400">Chargement…</div>
        ) : error ? (
          <div className="p-6 text-center text-sm text-rose-600">Impossible de charger les visites. Réessayez plus tard.</div>
        ) : data.recent.length === 0 ? (
          <div className="flex flex-col items-center px-4 py-7 text-center" data-testid="recent-empty">
            <Icons.FileText className="h-9 w-9 text-slate-300" />
            <p className="mt-2 font-semibold text-slate-700">Vos visites apparaîtront ici.</p>
            <p className="mt-0.5 text-sm text-slate-500">Une fois vos visites réalisées, elles seront listées ici.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.recent.map((v) => (
              <li key={v.id}>
                <button onClick={() => navigate(`/check/${v.id}`)} data-testid={`recent-visit-${v.reference}`}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 sm:px-5">
                  <CoverThumb path={v.site_cover_file_id ? `/files/${v.site_cover_file_id}/download` : null} alt={v.site_name} icon="MapPin" tile="bg-emerald-50 text-emerald-700" testId={`recent-${v.reference}`} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-slate-800">
                      <span className="font-mono-data text-xs text-slate-400">{v.reference}</span> · {v.client_name || "—"}
                    </p>
                    <p className="truncate text-sm text-slate-500">{v.site_name} · {fmtDate(v.date)}</p>
                  </div>
                  <div className="hidden gap-1 sm:flex">
                    {v.prestations?.slice(0, 3).map((p) => (
                      <span key={p} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                        {PRESTATIONS[p]?.name || p}
                      </span>
                    ))}
                  </div>
                  <StatusBadge status={v.status} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
